import React, { useState, useEffect } from "react";
import { Box, Typography, Button, CircularProgress } from "@mui/material";
import CurrentMonitoring from "../components/Monitor/CurrentMonitoring";
import MonitoringSignup from "../components/Monitor/MonitoringSignup";
import ViewMonitoringDetails from "../components/Monitor/ViewMonitoringDetails";

const MonitorPage = () => {
  const [monitoringStatus, setMonitoringStatus] = useState("loading");
  const [monitoringData, setMonitoringData] = useState([]);
  const [selectedMonitoring, setSelectedMonitoring] = useState(null);
  const [isSignupOpen, setIsSignupOpen] = useState(false);
  const [currentMonitoringId, setCurrentMonitoringId] = useState(null);
  const [editingMonitoringDetails, setEditingMonitoringDetails] =
    useState(null);
  const [isViewDetailsOpen, setIsViewDetailsOpen] = useState(false);

  useEffect(() => {
    fetchMonitoringData();
  }, []);

  const fetchMonitoringData = async () => {
    try {
      const userId = await localStorage.getItem("UserId"); // Assume userId is stored in localStorage
      console.log(userId);
      const response = await fetch(
        `https://api.hackerthread.com/api/monitoring/search?user_id=${userId}`
      );
      const data = await response.json();

      if (response.ok) {
        if (Array.isArray(data) && data.length > 0) {
          setMonitoringStatus("active");
          setMonitoringData(data);
        } else {
          setMonitoringStatus("inactive");
        }
      } else {
        throw new Error(data.message || "Failed to fetch monitoring data");
      }
    } catch (error) {
      console.error("Error fetching monitoring data:", error);
      setMonitoringStatus("inactive");
    }
  };

  const handleSetupMonitoring = (id) => {
    setCurrentMonitoringId(id);
    setEditingMonitoringDetails(null);
    setIsSignupOpen(true);
  };

  const handleActivateMonitoring = async () => {
    try {
      const userId = await localStorage.getItem("UserId");
      const expirationDate = new Date();
      expirationDate.setFullYear(expirationDate.getFullYear() + 1);
      const formattedExpiration = `${(expirationDate.getMonth() + 1)
        .toString()
        .padStart(2, "0")}/${expirationDate
        .getDate()
        .toString()
        .padStart(2, "0")}/${expirationDate.getFullYear()}`;

      const response = await fetch(
        "https://api.hackerthread.com/api/monitoring",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            user_id: Number(userId),
            expiration: formattedExpiration,
          }),
        }
      );

      if (response.ok) {
        await fetchMonitoringData();
      } else {
        throw new Error("Failed to activate monitoring");
      }
    } catch (error) {
      console.error("Error activating monitoring:", error);
    }
  };

  const handleAddNewMonitor = async () => {
    const newMonitoring = await handleActivateMonitoring();
    if (newMonitoring && newMonitoring.id) {
      setCurrentMonitoringId(newMonitoring.id);
      setIsSignupOpen(true);
    }
  };

  const handleEditMonitoring = (monitoringDetails) => {
    setCurrentMonitoringId(monitoringDetails.monitoring_id);
    setEditingMonitoringDetails(monitoringDetails);
    setIsSignupOpen(true);
  };

  const handleSaveMonitoring = async (formData) => {
    formData.monitoring_id = currentMonitoringId;
    try {
      const url = editingMonitoringDetails
        ? `https://api.hackerthread.com/api/monitoring-details/${editingMonitoringDetails.id}`
        : "https://api.hackerthread.com/api/monitoring-details";

      const method = editingMonitoringDetails ? "PUT" : "POST";

      const response = await fetch(url, {
        method: method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        await fetchMonitoringData();
        setIsSignupOpen(false);
        setCurrentMonitoringId(null);
        setEditingMonitoringDetails(null);
      } else {
        throw new Error("Failed to save monitoring details");
      }
    } catch (error) {
      console.error("Error saving monitoring details:", error);
    }
  };

  const handleViewDetails = (details) => {
    setSelectedMonitoring(details);
    setIsViewDetailsOpen(true);
  };

  if (monitoringStatus === "loading") {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (monitoringStatus === "error") {
    return (
      <Box sx={{ textAlign: "center", py: 4 }}>
        <Typography variant="h6" color="error">
          Error loading monitoring data. Please try again later.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: "lg", mx: "auto", p: 2, marginBottom: "60px" }}>
      <Typography variant="h4" gutterBottom>
        Monitoring
      </Typography>
      {monitoringStatus === "inactive" ? (
        <Box sx={{ textAlign: "center", py: 4 }}>
          <Typography variant="body1" gutterBottom>
            You don't have any active monitoring. Would you like to activate it?
          </Typography>
          <Button
            variant="contained"
            color="primary"
            onClick={handleActivateMonitoring}
          >
            Activate Monitoring
          </Button>
        </Box>
      ) : (
        <CurrentMonitoring
          data={monitoringData}
          setUpMonitor={handleSetupMonitoring}
          onAddNewMonitor={handleAddNewMonitor}
          onEditMonitoring={handleEditMonitoring}
          onViewDetails={handleViewDetails}
        />
      )}
      {isSignupOpen && (
        <MonitoringSignup
          open={isSignupOpen}
          onClose={() => {
            setIsSignupOpen(false);
            setCurrentMonitoringId(null);
            setEditingMonitoringDetails(null);
          }}
          monitoringId={currentMonitoringId}
          existingData={editingMonitoringDetails}
          onSave={handleSaveMonitoring}
        />
      )}
      {isViewDetailsOpen && (
        <ViewMonitoringDetails
          open={isViewDetailsOpen}
          onClose={() => setIsViewDetailsOpen(false)}
          details={selectedMonitoring}
        />
      )}
    </Box>
  );
};

export default MonitorPage;
